import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import AdminLoginForm from "@/components/AdminLoginForm";
import { adminFetch, getAdminToken, setAdminToken, clearAdminToken } from "@/lib/adminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Full Kenyan coast + Voi/Taveta inland corridor (Taita-Taveta County)
const MAP_BOUNDS = [
  [-4.90, 37.50],
  [-1.55, 41.80],
];

// Five coastal counties + Taita-Taveta (inland)
const COUNTIES = [
  { num: "001", name: "Mombasa",     bounds: [[-4.10, 39.60], [-3.96, 39.72]] },
  { num: "002", name: "Kwale",       bounds: [[-4.78, 38.45], [-3.95, 39.62]] },
  { num: "003", name: "Kilifi",      bounds: [[-4.00, 39.45], [-2.85, 40.30]] },
  { num: "004", name: "Tana River",  bounds: [[-2.90, 38.50], [-1.00, 40.55]] },
  { num: "005", name: "Lamu",        bounds: [[-2.70, 40.10], [-1.55, 41.80]] },
  { num: "006", name: "Taita-Taveta", bounds: [[-4.20, 37.55], [-2.90, 39.10]] },
];

const loadAsset = (url, type) => new Promise((resolve, reject) => {
  if (type === "css") {
    if (document.querySelector(`link[href="${url}"]`)) return resolve();
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = url;
    l.onload = resolve; l.onerror = reject;
    document.head.appendChild(l);
  } else {
    if (document.querySelector(`script[src="${url}"]`)) {
      let ticks = 0;
      const tick = () => {
        if (window.L && window.turf) return resolve();
        if (++ticks > 200) return reject(new Error(`timeout waiting ${url}`));
        setTimeout(tick, 50);
      };
      return tick();
    }
    const s = document.createElement("script");
    s.src = url; s.async = true;
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  }
});

const emptyDraft = () => ({ kind: "idle", points: [], center: null, radius_m: 0 });

export default function Coverage() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapElRef = useRef(null);
  const routeLayerRef = useRef(null);
  const ordersLayerRef = useRef(null);
  const userPinRef = useRef(null);
  const userAccRef = useRef(null);
  const draftLayerRef = useRef(null);
  const draftRef = useRef(emptyDraft());
  const serviceTargetRef = useRef(null);
  const countyOverlayRef = useRef(null);
  const countyCacheRef = useRef(new Map()); // "lat3,lng3" -> county name

  const [ready, setReady] = useState(false);
  const [draft, setDraft] = useState(emptyDraft()); // mirrors draftRef for UI
  const [savedCount, setSavedCount] = useState(0);
  const [adminMode, setAdminMode] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [status, setStatus] = useState("");
  const [userPos, setUserPos] = useState(null);
  const [userCounty, setUserCounty] = useState("");
  const [locating, setLocating] = useState(true);
  const [orders, setOrders] = useState([]);
  const [showOrders, setShowOrders] = useState(true);
  const [serviceTarget, setServiceTarget] = useState(null); // {lat, lng}
  const [serviceMoved, setServiceMoved] = useState(false);
  const [countyMenuOpen, setCountyMenuOpen] = useState(false);
  const [activeCounty, setActiveCounty] = useState(null); // num string or null

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    setAdminMode(q.get("admin") === "1");
    setHasToken(!!getAdminToken());
  }, []);

  // boot map
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadAsset("https://unpkg.com/leaflet@1.9.4/dist/leaflet.css", "css");
        await loadAsset("https://unpkg.com/leaflet@1.9.4/dist/leaflet.js", "js");
        await loadAsset("https://unpkg.com/@turf/turf@6.5.0/turf.min.js", "js");
      } catch (e) {
        setStatus("FAILED TO LOAD MAP ASSETS");
        return;
      }
      if (cancelled) return;
      const L = window.L;
      if (!mapElRef.current || mapRef.current) { setReady(true); return; }

      const map = L.map(mapElRef.current, { zoomControl: true });
      map.fitBounds(MAP_BOUNDS);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);

      const anchorStyle = {
        radius: 5,
        color: "#0A0A0A",
        fillColor: "#00A6D6",
        fillOpacity: 1,
        weight: 2,
      };
      [
        { name: "Kiunga",         coords: [-1.74, 41.485] },
        { name: "Lamu",           coords: [-2.272, 40.902] },
        { name: "Mambrui",        coords: [-3.10, 40.13] },
        { name: "Malindi",        coords: [-3.218, 40.117] },
        { name: "Watamu",         coords: [-3.355, 40.018] },
        { name: "Kilifi",         coords: [-3.633, 39.85] },
        { name: "Mtwapa",         coords: [-3.943, 39.738] },
        { name: "Kaloleni",       coords: [-3.815, 39.516] },
        { name: "Mazeras",        coords: [-3.978, 39.541] },
        { name: "Mombasa",        coords: [-4.043, 39.668] },
        { name: "Mariakani",      coords: [-3.857, 39.482] },
        { name: "Samburu",        coords: [-3.766, 39.262] },
        { name: "MacKinnon Road", coords: [-3.737, 39.046] },
        { name: "Taru",           coords: [-3.708, 38.795] },
        { name: "Maungu",         coords: [-3.563, 38.760] },
        { name: "Voi",            coords: [-3.397, 38.557] },
        { name: "Mwatate",        coords: [-3.504, 38.376] },
        { name: "Bura",           coords: [-3.488, 38.302] },
        { name: "Maktau",         coords: [-3.450, 38.100] },
        { name: "Mahoo",          coords: [-3.430, 37.860] },
        { name: "Taveta",         coords: [-3.398, 37.683] },
        { name: "Diani",          coords: [-4.32, 39.575] },
        { name: "Vanga",          coords: [-4.66, 39.21] },
      ].forEach(({ name, coords }) => {
        L.circleMarker(coords, anchorStyle).addTo(map)
          .bindTooltip(name, { permanent: true, direction: "right", className: "tn-anchor-label", offset: [8, 0] });
      });

      mapRef.current = map;
      routeLayerRef.current = L.layerGroup().addTo(map);
      ordersLayerRef.current = L.layerGroup().addTo(map);
      draftLayerRef.current = L.layerGroup().addTo(map);

      setReady(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // load saved coverage routes
  const loadRoutes = useCallback(async () => {
    if (!routeLayerRef.current || !window.L) return;
    try {
      const res = await fetch(`${API}/coverage`);
      if (!res.ok) throw new Error("fetch failed");
      const data = await res.json();
      const L = window.L;
      routeLayerRef.current.clearLayers();
      data.forEach((r) => {
        if (r.buffer_coords && r.buffer_coords.length) {
          const rings = r.buffer_coords.map((ring) => ring.map(([lng, lat]) => [lat, lng]));
          L.polygon(rings, {
            color: "#1FB6E0",
            weight: 3,
            dashArray: "8,6",
            fillColor: "#1FB6E0",
            fillOpacity: r.kind === "circle" ? 0.10 : 0.12,
          }).addTo(routeLayerRef.current);
        }
        if (r.kind === "circle" && r.center) {
          const [lng, lat] = r.center;
          L.circleMarker([lat, lng], {
            radius: 5, color: "#0A0A0A", fillColor: "#1FB6E0", fillOpacity: 1, weight: 2,
          }).addTo(routeLayerRef.current)
            .bindTooltip(`HUB · ${Math.round(r.radius_m || 0)}m`, { permanent: false, direction: "top" });
        } else if (r.line_coords && r.line_coords.length >= 2) {
          const lineLatLng = r.line_coords.map(([lng, lat]) => [lat, lng]);
          L.polyline(lineLatLng, { color: "#0A0A0A", weight: 3, dashArray: "6,4" })
            .addTo(routeLayerRef.current);
        }
      });
      setSavedCount(data.length);
    } catch (e) {
      console.warn("could not load saved routes", e);
    }
  }, []);

  // load orders (admin only)
  const loadOrders = useCallback(async () => {
    if (!ordersLayerRef.current || !window.L) return;
    if (!getAdminToken()) {
      ordersLayerRef.current.clearLayers();
      setOrders([]);
      return;
    }
    try {
      const res = await adminFetch(`${API}/orders`);
      if (res.status === 401 || res.status === 403) {
        clearAdminToken();
        setHasToken(false);
        return;
      }
      if (!res.ok) throw new Error("fetch failed");
      const data = await res.json();
      const L = window.L;
      ordersLayerRef.current.clearLayers();
      if (showOrders) {
        data.forEach((o) => {
          if (typeof o.lat === "number" && typeof o.lng === "number") {
            L.circleMarker([o.lat, o.lng], {
              radius: 7, color: "#E0218A", fillColor: "#E0218A", fillOpacity: 0.85, weight: 2,
            }).addTo(ordersLayerRef.current)
              .bindTooltip(
                `${o.name} · ${o.plan}<br>${o.phone}`,
                { permanent: false, direction: "top", className: "tn-anchor-label" }
              );
          }
        });
      }
      setOrders(data);
    } catch (e) {
      console.warn("orders load failed", e);
    }
  }, [showOrders]);

  useEffect(() => { if (ready) loadRoutes(); }, [ready, loadRoutes]);
  useEffect(() => { if (ready && adminMode && hasToken) loadOrders(); }, [ready, adminMode, hasToken, loadOrders]);

  // -------- reverse geocode user pin → county (Nominatim, cached) --------
  const resolveCounty = useCallback(async (lat, lng) => {
    const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
    if (countyCacheRef.current.has(key)) {
      setUserCounty(countyCacheRef.current.get(key));
      return;
    }
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat.toFixed(4)}&lon=${lng.toFixed(4)}&format=json&zoom=10&addressdetails=1`;
      const res = await fetch(url, { headers: { "Accept": "application/json" } });
      if (!res.ok) throw new Error("nominatim failed");
      const data = await res.json();
      const a = data.address || {};
      const county = a.county || a.state_district || a.region || a.state || "";
      const clean = county.replace(/\s*County$/i, "").trim();
      countyCacheRef.current.set(key, clean);
      setUserCounty(clean);
    } catch (e) {
      console.warn("reverse geocode failed", e);
    }
  }, []);

  // -------- county filter --------
  const focusCounty = useCallback((county) => {
    if (!mapRef.current || !window.L) return;
    const map = mapRef.current;
    if (countyOverlayRef.current) {
      map.removeLayer(countyOverlayRef.current);
      countyOverlayRef.current = null;
    }
    if (!county) {
      map.fitBounds(MAP_BOUNDS);
      setActiveCounty(null);
      return;
    }
    map.fitBounds(county.bounds, { padding: [40, 40] });
    countyOverlayRef.current = window.L.rectangle(county.bounds, {
      color: "#1FB6E0",
      weight: 3,
      dashArray: "8,6",
      fillColor: "#1FB6E0",
      fillOpacity: 0.05,
      interactive: false,
    }).addTo(map);
    setActiveCounty(county.num);
  }, []);
  const placeServicePin = useCallback((lat, lng) => {
    if (!mapRef.current || !window.L) return;
    const L = window.L;
    const map = mapRef.current;
    if (serviceTargetRef.current) map.removeLayer(serviceTargetRef.current);
    serviceTargetRef.current = L.circleMarker([lat, lng], {
      radius: 10,
      color: "#0A0A0A",
      fillColor: "#3FAE2A",
      fillOpacity: 1,
      weight: 3,
    })
      .addTo(map)
      .bindTooltip("REQUEST SERVICE HERE", {
        permanent: true,
        direction: "bottom",
        className: "tn-anchor-label",
        offset: [0, 12],
      });
  }, []);

  const handlePublicMapClick = useCallback((e) => {
    if (draftRef.current.kind !== "idle") return; // admin tool active
    const { lat, lng } = e.latlng;
    setServiceTarget({ lat, lng });
    setServiceMoved(true);
    placeServicePin(lat, lng);
  }, [placeServicePin]);

  // attach click handler only in public (non-admin) view
  useEffect(() => {
    if (!ready || adminMode) return;
    const map = mapRef.current;
    if (!map) return;
    map.on("click", handlePublicMapClick);
    return () => { map.off("click", handlePublicMapClick); };
  }, [ready, adminMode, handlePublicMapClick]);

  // user geolocation pin
  const locateUser = useCallback((zoom = true) => {
    if (!navigator.geolocation || !mapRef.current || !window.L) {
      setLocating(false);
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const acc = pos.coords.accuracy || 50;
        const L = window.L;
        const map = mapRef.current;

        if (userPinRef.current) map.removeLayer(userPinRef.current);
        if (userAccRef.current) map.removeLayer(userAccRef.current);

        userAccRef.current = L.circle([lat, lng], {
          radius: acc, color: "#00A6D6", weight: 1, fillColor: "#00A6D6", fillOpacity: 0.12,
        }).addTo(map);

        userPinRef.current = L.circleMarker([lat, lng], {
          radius: 8, color: "#0A0A0A", fillColor: "#00A6D6", fillOpacity: 1, weight: 3,
        }).addTo(map).bindTooltip("YOU ARE HERE", { permanent: true, direction: "top", className: "tn-anchor-label", offset: [0, -10] });

        setUserPos({ lat, lng, acc });
        setLocating(false);
        // seed the service target to the user's pin (until they move it)
        if (!serviceMoved) {
          setServiceTarget({ lat, lng });
          placeServicePin(lat, lng);
        }
        // resolve county asynchronously (cached by ~3-decimal lat/lng)
        resolveCounty(lat, lng);
        if (zoom) map.setView([lat, lng], 14);
      },
      () => { setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, []);

  useEffect(() => { if (ready) locateUser(true); }, [ready, locateUser]);

  // -------- drawing --------
  const updateDraft = (next) => {
    draftRef.current = next;
    setDraft(next);
  };

  const renderDraft = () => {
    const L = window.L;
    draftLayerRef.current.clearLayers();
    const d = draftRef.current;
    if (d.kind === "line") {
      d.points.forEach(([lng, lat]) =>
        L.circleMarker([lat, lng], {
          radius: 4, color: "#0A0A0A", fillColor: "#FFFFFF", fillOpacity: 1, weight: 2,
        }).addTo(draftLayerRef.current)
      );
      if (d.points.length >= 2) {
        const latlngs = d.points.map(([lng, lat]) => [lat, lng]);
        L.polyline(latlngs, { color: "#00A6D6", weight: 4 }).addTo(draftLayerRef.current);
      }
    } else if (d.kind === "circle") {
      if (d.center) {
        const [lng, lat] = d.center;
        L.circleMarker([lat, lng], {
          radius: 5, color: "#0A0A0A", fillColor: "#FFFFFF", fillOpacity: 1, weight: 2,
        }).addTo(draftLayerRef.current);
        if (d.radius_m > 0) {
          L.circle([lat, lng], {
            radius: d.radius_m, color: "#00A6D6", weight: 3, fillColor: "#00A6D6", fillOpacity: 0.15,
          }).addTo(draftLayerRef.current);
        }
      }
    }
  };

  const onMapClick = useCallback((e) => {
    const { lat, lng } = e.latlng;
    const d = draftRef.current;
    if (d.kind === "line") {
      const next = { ...d, points: [...d.points, [lng, lat]] };
      updateDraft(next);
      renderDraft();
      setStatus(`POINT ${next.points.length} ADDED · MIN 2`);
    } else if (d.kind === "circle") {
      if (!d.center) {
        updateDraft({ ...d, center: [lng, lat], radius_m: 0 });
        renderDraft();
        setStatus("CLICK ANY POINT ON THE EDGE TO SET RADIUS");
      } else {
        const radius = mapRef.current.distance([d.center[1], d.center[0]], [lat, lng]);
        updateDraft({ ...d, radius_m: radius });
        renderDraft();
        setStatus(`RADIUS: ${Math.round(radius)} m · SAVE OR CLICK AGAIN TO ADJUST`);
      }
    }
  }, []);

  const startTool = (kind) => {
    if (!mapRef.current) return;
    mapRef.current.off("click", onMapClick);
    draftLayerRef.current.clearLayers();
    updateDraft({ ...emptyDraft(), kind });
    mapRef.current.on("click", onMapClick);
    if (kind === "line") setStatus("CLICK MAP TO ADD POINTS · MIN 2 · THEN SAVE");
    else if (kind === "circle") setStatus("CLICK MAP TO PLACE HUB CENTER");
  };

  const cancelTool = () => {
    if (mapRef.current) mapRef.current.off("click", onMapClick);
    draftLayerRef.current.clearLayers();
    updateDraft(emptyDraft());
    setStatus("");
  };

  const saveDraft = async () => {
    const turf = window.turf;
    const d = draftRef.current;
    let payload = null;

    if (d.kind === "line") {
      if (d.points.length < 2) {
        setStatus("NEED AT LEAST 2 POINTS");
        return;
      }
      const line = turf.lineString(d.points);
      const buffered = turf.buffer(line, 50, { units: "meters" });
      const bufferCoords =
        buffered.geometry.type === "Polygon"
          ? buffered.geometry.coordinates
          : buffered.geometry.coordinates[0];
      payload = {
        name: `Backbone ${new Date().toISOString().slice(0, 16)}`,
        kind: "line",
        line_coords: [...d.points],
        buffer_coords: bufferCoords,
        buffer_meters: 50,
      };
    } else if (d.kind === "circle") {
      if (!d.center || !(d.radius_m > 0)) {
        setStatus("CLICK ONCE FOR CENTER, AGAIN FOR EDGE");
        return;
      }
      const circ = turf.circle(d.center, d.radius_m / 1000, { steps: 64, units: "kilometers" });
      payload = {
        name: `Hub ${new Date().toISOString().slice(0, 16)}`,
        kind: "circle",
        center: d.center,
        radius_m: d.radius_m,
        buffer_coords: circ.geometry.coordinates,
        buffer_meters: 0,
      };
    } else {
      return;
    }

    setStatus("SAVING...");
    try {
      const res = await adminFetch(`${API}/coverage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.status === 401 || res.status === 403) {
        clearAdminToken();
        setHasToken(false);
        setStatus("SESSION EXPIRED. SIGN IN AGAIN.");
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      cancelTool();
      await loadRoutes();
      setStatus("SAVED.");
      setTimeout(() => setStatus(""), 2000);
    } catch (e) {
      console.error(e);
      setStatus("SAVE FAILED");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete ALL saved coverage routes?")) return;
    setStatus("CLEARING...");
    try {
      const res = await adminFetch(`${API}/coverage`, { method: "DELETE" });
      if (res.status === 401 || res.status === 403) {
        clearAdminToken();
        setHasToken(false);
        setStatus("SESSION EXPIRED. SIGN IN AGAIN.");
        return;
      }
      await loadRoutes();
      setStatus("CLEARED.");
      setTimeout(() => setStatus(""), 2000);
    } catch (e) {
      setStatus("CLEAR FAILED");
    }
  };

  const requestServiceHere = () => {
    const pos = serviceTarget || userPos;
    if (!pos) return;
    const q = new URLSearchParams({
      lat: pos.lat.toFixed(6),
      lng: pos.lng.toFixed(6),
    });
    navigate(`/order.html?${q.toString()}`);
  };

  const resetServiceTarget = () => {
    if (!userPos) return;
    setServiceTarget({ lat: userPos.lat, lng: userPos.lng });
    setServiceMoved(false);
    placeServicePin(userPos.lat, userPos.lng);
  };

  const onLoginSuccess = (token) => {
    setAdminToken(token);
    setHasToken(true);
    setStatus("");
  };

  const onLogout = () => {
    clearAdminToken();
    setHasToken(false);
    cancelTool();
    if (ordersLayerRef.current) ordersLayerRef.current.clearLayers();
    setOrders([]);
  };

  const ordersWithCoords = orders.filter(
    (o) => typeof o.lat === "number" && typeof o.lng === "number"
  );

  return (
    <div className="tn-cov-wrap" data-testid="coverage-page">
      <TopBar active="coverage" sticky />

      <div className="tn-cov-head" data-testid="coverage-head">
        <div className="tn-cov-title">
          <span className="accent">// </span>COVERAGE ENGINE
          <span className="tn-cov-counties-wrap">
            <button
              type="button"
              className={`tn-cov-counties ${countyMenuOpen ? "is-open" : ""}`}
              onClick={() => setCountyMenuOpen((v) => !v)}
              data-testid="coverage-counties"
              aria-haspopup="listbox"
              aria-expanded={countyMenuOpen}
            >
              COVERAGE: {activeCounty ? `#${activeCounty}` : "#001 – #006"} ▾
            </button>
            {countyMenuOpen && (
              <div className="tn-cov-county-menu" role="listbox" data-testid="county-menu">
                <button
                  type="button"
                  className={`tn-cov-county-item ${!activeCounty ? "is-active" : ""}`}
                  onClick={() => { focusCounty(null); setCountyMenuOpen(false); }}
                  data-testid="county-item-all"
                >
                  ALL · #001 – #006
                </button>
                {COUNTIES.map((c) => (
                  <button
                    key={c.num}
                    type="button"
                    className={`tn-cov-county-item ${activeCounty === c.num ? "is-active" : ""}`}
                    onClick={() => { focusCounty(c); setCountyMenuOpen(false); }}
                    data-testid={`county-item-${c.num}`}
                  >
                    #{c.num} · {c.name}
                  </button>
                ))}
              </div>
            )}
          </span>
        </div>
        <div className="tn-cov-head-right">
          <span className="tn-eyebrow">
            {savedCount} ROUTE{savedCount === 1 ? "" : "S"} ACTIVE · 50M BUFFER {adminMode ? "· ADMIN" : ""}
          </span>
          {adminMode && hasToken && (
            <button
              type="button"
              className="tn-btn tn-back-btn"
              onClick={onLogout}
              data-testid="admin-logout-btn"
            >
              LOGOUT
            </button>
          )}
          <button
            type="button"
            className="tn-btn tn-back-btn"
            onClick={() => navigate("/")}
            data-testid="back-home-btn"
          >
            ← BACK
          </button>
        </div>
      </div>

      <div className="tn-map" data-testid="coverage-map">
        <div ref={mapElRef} style={{ position: "absolute", inset: 0 }} />

        <div className="tn-legend" data-testid="map-legend">
          <div className="row"><span className="swatch" /> Backbone</div>
          <div className="row"><span className="swatch poly" /> Coverage</div>
          <div className="row"><span className="swatch dot" /> Your Position</div>
          <div className="row"><span className="swatch dot green" /> Service Target</div>
          {adminMode && hasToken && (
            <div className="row"><span className="swatch dot pink" /> Order Pin</div>
          )}
        </div>

        {!adminMode && (
          <div className="tn-locate-panel" data-testid="locate-panel">
            <div className="tn-eyebrow">/ YOUR PIN</div>
            {locating && <div className="tn-locate-msg">Locating you on the map...</div>}
            {!locating && userPos && (
              <>
                <div className="tn-locate-coords" data-testid="locate-coords">
                  {userPos.lat.toFixed(5)}, {userPos.lng.toFixed(5)}
                </div>
                {userCounty && (
                  <div className="tn-locate-county" data-testid="locate-county">
                    in <b>{userCounty}</b> County
                  </div>
                )}
              </>
            )}
            {!locating && !userPos && (
              <>
                <div className="tn-locate-msg">GPS unavailable.</div>
                <button
                  type="button"
                  className="tn-btn"
                  onClick={() => locateUser(true)}
                  data-testid="retry-locate-btn"
                >
                  RETRY LOCATE
                </button>
              </>
            )}

            <div className="tn-locate-hint" data-testid="locate-move-hint">
              ↳ or click anywhere on the map to move to where you need service
            </div>

            {serviceTarget && (
              <div className="tn-locate-target" data-testid="service-target-block">
                <div className="tn-eyebrow">/ SERVICE TARGET</div>
                <div className="tn-locate-coords tn-locate-coords-green" data-testid="service-target-coords">
                  {serviceTarget.lat.toFixed(5)}, {serviceTarget.lng.toFixed(5)}
                </div>
                {serviceMoved && userPos && (
                  <button
                    type="button"
                    className="tn-locate-reset"
                    onClick={resetServiceTarget}
                    data-testid="reset-service-target-btn"
                  >
                    [ ↩ reset to my pin ]
                  </button>
                )}
              </div>
            )}

            {(serviceTarget || userPos) && (
              <button
                type="button"
                className="tn-btn is-accent tn-locate-cta"
                onClick={requestServiceHere}
                data-testid="request-service-here-btn"
              >
                REQUEST SERVICE HERE →
              </button>
            )}
            {status && <div className="tn-locate-status">{status}</div>}
          </div>
        )}

        {adminMode && !hasToken && (
          <div className="tn-admin-panel" data-testid="admin-panel-locked">
            <AdminLoginForm onSuccess={onLoginSuccess} />
          </div>
        )}

        {adminMode && hasToken && (
          <div className="tn-admin-panel" data-testid="admin-panel">
            <h3>Admin <span className="accent">//</span> Draw</h3>
            <p>Tools: 50m wayleave line, or hub coverage circle.</p>
            <div className="tn-btn-row">
              {draft.kind === "idle" && (
                <>
                  <button className="tn-btn is-accent" onClick={() => startTool("line")} data-testid="admin-tool-line">
                    + Line
                  </button>
                  <button className="tn-btn is-accent" onClick={() => startTool("circle")} data-testid="admin-tool-circle">
                    + Circle
                  </button>
                </>
              )}
              {draft.kind !== "idle" && (
                <>
                  <button className="tn-btn is-accent" onClick={saveDraft} data-testid="admin-save-draft">
                    Save {draft.kind === "line" ? "Line" : "Circle"}
                  </button>
                  <button className="tn-btn" onClick={cancelTool} data-testid="admin-cancel-tool">
                    Cancel
                  </button>
                </>
              )}
              <button className="tn-btn" onClick={clearAll} data-testid="admin-clear-all">
                Clear All
              </button>
            </div>

            {/* orders mini panel */}
            <div className="tn-admin-orders" data-testid="admin-orders">
              <div className="tn-admin-orders-head">
                <span className="tn-eyebrow">/ ORDER HEATMAP</span>
                <button
                  type="button"
                  className="tn-admin-orders-toggle"
                  onClick={() => setShowOrders((v) => !v)}
                  data-testid="admin-orders-toggle"
                >
                  [{showOrders ? "HIDE" : "SHOW"}]
                </button>
              </div>
              <div className="tn-admin-orders-stat" data-testid="admin-orders-count">
                {orders.length} ORDER{orders.length === 1 ? "" : "S"} ·{" "}
                {ordersWithCoords.length} WITH PIN
              </div>
              {orders.length > 0 && (
                <ul className="tn-admin-orders-list" data-testid="admin-orders-list">
                  {orders.slice(0, 5).map((o) => (
                    <li key={o.id}>
                      <b>{o.name}</b> · {o.plan.split(" - ")[0]}
                      {typeof o.lat === "number" ? " · 📍" : " · —"}
                    </li>
                  ))}
                  {orders.length > 5 && <li>+{orders.length - 5} more...</li>}
                </ul>
              )}
            </div>

            {status && <p className="tn-admin-status" data-testid="admin-status">{status}</p>}
          </div>
        )}
      </div>

      <footer className="tn-foot" data-testid="coverage-footer">
        <div>Tandaa Networks Ltd · Kilifi, Kenya</div>
        <div><a href="/" data-testid="footer-home-link">[ ← HOME ]</a></div>
        <div><a href="/order.html" data-testid="footer-order-link">[ ORDER → ]</a></div>
        <a
          href="tel:+254730720720"
          className="tn-btn is-green tn-foot-call-btn"
          data-testid="coverage-footer-call-btn"
        >
          Call us →
        </a>
      </footer>
    </div>
  );
}
